// Typed client for the standing-agent ("Orion Pulsars") endpoints
// (StandingAgentsController, api/standing-agents). Thin wrappers over apiFetch so
// the Pulsars pages never repeat path / shape literals — mirrors playbookApi.ts.

import type {
  ActivityPage,
  AgentName,
  AgentOption,
  BriefingPreview,
  CheckInDetail,
  CheckInStarted,
  DecideProposalsRequest,
  DecisionResult,
  ProposalBatch,
  SaveStandingAgentRequest,
  SaveStandingAgentResult,
  StandingAgentDetail,
  StandingAgentSummary,
  StandingAttention,
  StandingLearning,
  StandingMemory,
  StandingMeta,
  StandingReport,
  StandingWebhook,
} from '@shared/api-types';
import { apiFetch } from './apiClient';

const BASE = '/api/standing-agents';

/** The Pulsars list. */
export const listStandingAgents = (signal?: AbortSignal) =>
  apiFetch<StandingAgentSummary[]>(BASE, { signal });

/** "Needs you" + the nav badge count (`enabled: false` when the feature is off). */
export const getAttention = (signal?: AbortSignal) => apiFetch<StandingAttention>(`${BASE}/attention`, { signal });

/** The home dashboard's Pulsars report: status now plus 7 days of check-ins, proposals and actions. */
export const getReport = (signal?: AbortSignal) => apiFetch<StandingReport>(`${BASE}/report`, { signal });

/** Defaults, tenant time zone and the caller's role for the create flow. */
export const getStandingMeta = (signal?: AbortSignal) => apiFetch<StandingMeta>(`${BASE}/meta`, { signal });

/** The agents a Pulsar can use — names only, so the picker loads at once. */
export const getAgentOptions = (signal?: AbortSignal) => apiFetch<AgentName[]>(`${BASE}/agent-options`, { signal });

/** One agent's details (description, which tools can change things), loaded when it is chosen. */
export const getAgentOption = (slug: string, signal?: AbortSignal) =>
  apiFetch<AgentOption>(`${BASE}/agent-options/${encodeURIComponent(slug)}`, { signal });

/** Workspace detail. */
export const getStandingAgent = (id: string, signal?: AbortSignal) =>
  apiFetch<StandingAgentDetail>(`${BASE}/${id}`, { signal });

/** Create a draft. */
export const createStandingAgent = (body: SaveStandingAgentRequest) =>
  apiFetch<SaveStandingAgentResult>(BASE, { method: 'POST', body });

/** Update settings. */
export const updateStandingAgent = (id: string, body: SaveStandingAgentRequest) =>
  apiFetch<SaveStandingAgentResult>(`${BASE}/${id}`, { method: 'PUT', body });

/** Delete. */
export const deleteStandingAgent = (id: string) => apiFetch<void>(`${BASE}/${id}`, { method: 'DELETE' });

/** Lifecycle: start / pause / resume / stop. */
export const startStandingAgent = (id: string) => apiFetch<void>(`${BASE}/${id}/start`, { method: 'POST' });
export const pauseStandingAgent = (id: string) => apiFetch<void>(`${BASE}/${id}/pause`, { method: 'POST' });
export const resumeStandingAgent = (id: string) => apiFetch<void>(`${BASE}/${id}/resume`, { method: 'POST' });
export const stopStandingAgent = (id: string) => apiFetch<void>(`${BASE}/${id}/stop`, { method: 'POST' });

/** Check in now; `trial` leaves the schedule (and a draft) alone. */
export const checkInNow = (id: string, trial: boolean) =>
  apiFetch<CheckInStarted>(`${BASE}/${id}/check-in-now${trial ? '?trial=true' : ''}`, { method: 'POST' });

/** (Re)generate the trigger secret — plaintext shown once. */
export const regenerateStandingWebhook = (id: string) =>
  apiFetch<StandingWebhook>(`${BASE}/${id}/webhook-secret`, { method: 'POST' });

/** Activity timeline (newest first). */
export const getActivity = (id: string, cursor?: string | null, signal?: AbortSignal) =>
  apiFetch<ActivityPage>(`${BASE}/${id}/activity${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`, { signal });

/** One check-in expanded. */
export const getCheckIn = (id: string, cycleId: string, signal?: AbortSignal) =>
  apiFetch<CheckInDetail>(`${BASE}/${id}/activity/${cycleId}`, { signal });

/** Exactly what the next check-in will be told. */
export const getBriefingPreview = (id: string, signal?: AbortSignal) =>
  apiFetch<BriefingPreview>(`${BASE}/${id}/briefing-preview`, { signal });

/** Notes, what it has learned, open questions. */
export const getMemory = (id: string, signal?: AbortSignal) => apiFetch<StandingMemory>(`${BASE}/${id}/memory`, { signal });

/** Edit / delete a learned entry. */
export const updateLearning = (id: string, learningId: string, text: string) =>
  apiFetch<StandingLearning>(`${BASE}/${id}/learnings/${learningId}`, { method: 'PUT', body: { text } });
export const deleteLearning = (id: string, learningId: string) =>
  apiFetch<void>(`${BASE}/${id}/learnings/${learningId}`, { method: 'DELETE' });

/** Answer a question the pulsar asked. */
export const answerQuestion = (id: string, questionId: string, answer: string) =>
  apiFetch<StandingLearning>(`${BASE}/${id}/questions/${questionId}/answer`, { method: 'POST', body: { answer } });

/** The Proposals tab (open batches first). */
export const listBatches = (id: string, signal?: AbortSignal) => apiFetch<ProposalBatch[]>(`${BASE}/${id}/batches`, { signal });

/** One batch (review surface). */
export const getBatch = (batchId: string, signal?: AbortSignal) =>
  apiFetch<ProposalBatch>(`${BASE}/batches/${batchId}`, { signal });

/** Approve / deny / edit — individually or "all remaining" — in one transaction. */
export const decideProposals = (batchId: string, body: DecideProposalsRequest) =>
  apiFetch<DecisionResult>(`${BASE}/batches/${batchId}/decisions`, { method: 'POST', body });

/** "Run approved now". */
export const runApproved = (batchId: string) => apiFetch<ProposalBatch>(`${BASE}/batches/${batchId}/run`, { method: 'POST' });

/** Undo a decision before the approved actions start. */
export const undoProposal = (proposalId: string) =>
  apiFetch<ProposalBatch>(`${BASE}/proposals/${proposalId}/undo`, { method: 'POST' });
